import {Component, EventEmitter, Input, OnChanges, Output, TemplateRef} from '@angular/core';
import {TranslateService} from '@ox/translate';
import {WarrantyPayload} from '@ox/typings';
import {OX_KEYS} from '@ox/config';
import {AutoDestroy, AppConfigService} from '@ox/utils';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject, take, takeWhile} from 'rxjs';
import {GfaText, RecallPayload} from './recall-payload.interface';
import {formatDate} from '@angular/common';
import {isEqual} from 'lodash';

@Component({
  selector: 'webu-tab-detail-item',
  templateUrl: './tab-detail-item.component.html'
})

export class TabDetailItemComponent implements OnChanges {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Input() checkCompleteIncomplete: string | null; //marker that checks what tab the user is in
  @Input() link = false;
  @Input() data: RecallPayload | WarrantyPayload | undefined = undefined;
  @Output() detailsClick = new EventEmitter<RecallPayload | WarrantyPayload | undefined>();
  upperLabel: string;
  mainLabel = '';
  lowerLabel: string;
  modalRef: BsModalRef<unknown>;
  gfaTextPerLang?: GfaText;
  warranties?: WarrantyPayload;
  _date = '' as string | undefined;
  _mileage = '' as string | undefined;
  warrantyCheck = false;
  public endDate = '';
  public endMileage = '';
  public warrantiesData: WarrantyPayload;
  public mainModalLabel = '';

  constructor(
    private translate: TranslateService,
    private modalService: BsModalService,
    private appConfig: AppConfigService
  ) {
  }


  ngOnChanges() {
    if (this.isWarranty(this.data)) {
      this.warrantyCheck = true;
      this.processWarranties();
    } else {
      if ((this.data as RecallPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.GFA_NUMBER]) {
        this.processRecalls();
      }
    }
  }

  public isWarranty(data: RecallPayload | WarrantyPayload | undefined): data is WarrantyPayload {
    return (data as WarrantyPayload)?.endMileage !== undefined;
  }

  async processRecalls() {
    //Returns desired language else returns english or the one language available
    const gfaLang = (item: {langCode: string;}) => item.langCode === this.appConfig.bml.lang.toUpperCase();
    const gfaTexts = (this.data as RecallPayload).gfaTexts || [];
    this.gfaTextPerLang = (Array.isArray(gfaTexts) && gfaTexts.length <= 0) ? undefined :
    (gfaTexts.length === 1) ?
    gfaTexts[0] :
    gfaTexts.some(gfaLang) ?
    gfaTexts.find(gfaLang) :
    gfaTexts.filter(
      (item) => item.langCode.toLowerCase().match(this.appConfig.bml.lang.toLowerCase()) )[0];



    this.mainLabel = this.gfaTextPerLang?.[OX_KEYS.TAB_DETAIL_ITEM.SUBJECT]
      ? this.gfaTextPerLang?.[OX_KEYS.TAB_DETAIL_ITEM.SUBJECT]
      : '';
    this.upperLabel = (this.data as RecallPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.CLASSIFICATION]
      ? (this.data as RecallPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.CLASSIFICATION]
      : '';

    const getDate = (_data: RecallPayload | undefined) => { // previously named data:any
      const closingDate = (this.data as RecallPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.VIN_STATUS_INFO]?.[OX_KEYS.TAB_DETAIL_ITEM.CLOSING_DATE];
      const validFromDate = (this.data as RecallPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.VIN_STATUS_INFO]?.[OX_KEYS.TAB_DETAIL_ITEM.RELEASE_DATE];

      if (closingDate) return closingDate;
      else if (validFromDate) return validFromDate;
      else return '';
    };

    this._date = getDate((this.data as RecallPayload));

    this.translate
      .get([OX_KEYS.TAB_DETAIL_ITEM.RECALL_CENTER_RELEASED, OX_KEYS.TAB_DETAIL_ITEM.RECALL_CENTER_COMPLETED], {
        date: this._date
      })
      .pipe(takeWhile(res => !isEqual(res,[OX_KEYS.TAB_DETAIL_ITEM.RECALL_CENTER_RELEASED, OX_KEYS.TAB_DETAIL_ITEM.RECALL_CENTER_COMPLETED])))
      .subscribe((cmsObj: any) => {
          const getLowerLabel = (cmsObj: any): string => {
          const recallCenter_completed = (this.data as RecallPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.VIN_STATUS_INFO]?.[OX_KEYS.TAB_DETAIL_ITEM.CLOSING_DATE];
          const recallCenter_released = (this.data as RecallPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.VIN_STATUS_INFO]?.[OX_KEYS.TAB_DETAIL_ITEM.RELEASE_DATE];

          if (recallCenter_completed) return cmsObj?.recallCenter_completed;
          else if (recallCenter_released) return cmsObj?.recallCenter_released;
          else return '';
        };

        this.lowerLabel = getLowerLabel(cmsObj);
      });
  }

  processWarranties() {
    this.warranties = (this.data as WarrantyPayload);
    if (this.warranties) {
      this.mainLabel = this.warranties?.[OX_KEYS.TAB_DETAIL_ITEM.DESCRIPTION]
        ? this.warranties?.[OX_KEYS.TAB_DETAIL_ITEM.DESCRIPTION] : '';
      this.mainModalLabel = (this.warranties.specialCoverageData && this.warranties.specialCoverageData[0]?.subject)
        ? this.mainLabel + ' ' + this.warranties.specialCoverageData[0].subject : this.mainLabel;
    }
    const getEndDate = () => {
      const date = new Date((this.data as WarrantyPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.END_DATE]);
      return (formatDate(date, 'longDate', this.appConfig.locale));
    };

    const getEndMileage = () => {
      return (this.data as WarrantyPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.END_MILEAGE];
    };

    this.endDate = getEndDate();
    this.endMileage = getEndMileage();
    this.translate
      .get([OX_KEYS.TAB_DETAIL_ITEM.WARRANTY_ACTIVE_LABEL, OX_KEYS.TAB_DETAIL_ITEM.WARRANTY_EXPIRED_LABEL], {
        endDate: this.endDate,
        endMileage: this.endMileage
      })
      .pipe(take(1))
      .subscribe((cmsObj: any) => {

        const getLowerLabel = (cmsObj: any) => {
          const warrantyCenter_status = (this.data as WarrantyPayload)?.[OX_KEYS.TAB_DETAIL_ITEM.STATUS];

          if (warrantyCenter_status === 'Expired') return cmsObj?.warrantyCenter_expired;
          else if (warrantyCenter_status) return cmsObj?.warrantyCenter_active;
          else return '';
        };

        this.lowerLabel = getLowerLabel(cmsObj);
      });
  }

  // ngx bootstrap service to invoke a new modal
  openModal(template: TemplateRef<any>) {
    this.modalRef = this.modalService.show(template);
    this.detailsClick.emit(this.data);
  }
}
